/** @jsxImportSource @emotion/react */
import { formatCalendarDate } from 'helpers/dates'
import { downloadEobPdf } from 'helpers/eob'
import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import { NeedsWorkParkedRemit } from 'store/types'

const money = (value?: number | null): string =>
  value === null || value === undefined ? '—' : `$${Number(value).toFixed(2)}`

const cellCss = { padding: '4px 8px', fontSize: '0.8rem', borderTop: '1px solid #e9ecef' }

/**
 * How the payer applied a parked remit, straight from the 835 — for the team to
 * see while nothing is posted from it.
 */
export const ParkedRemitTable: React.FC<{ remit: NeedsWorkParkedRemit }> = ({ remit }) => {
  const [eobLoading, setEobLoading] = useState(false)
  const facts = [
    remit.payerName,
    remit.checkNumber ? `Check/EFT ${remit.checkNumber}` : null,
    remit.checkDate ? formatCalendarDate(remit.checkDate) : null,
    remit.claimNumber ? `Claim ${remit.claimNumber}` : null,
    remit.payerClaimControlNumber ? `Payer claim ${remit.payerClaimControlNumber}` : null,
  ].filter(Boolean)

  return (
    <div css={{ background: '#fff', border: '1px solid #e3e6ea', borderRadius: 5, padding: '8px 12px', marginTop: 8 }}>
      <div className="d-flex align-items-center justify-content-between" css={{ gap: 12 }}>
        <div css={{ fontSize: '0.8rem' }}>
          <span css={{ fontWeight: 600 }}>
            {remit.parked ? 'Parked remit — nothing posted' : 'Remit'}
          </span>
          <span className="text-muted"> · {facts.join(' · ')}</span>
        </div>
        {remit.eraId && (
          <Button
            size="sm"
            variant="outline-dark"
            disabled={eobLoading}
            css={{ whiteSpace: 'nowrap', padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
            onClick={async () => {
              setEobLoading(true)
              await downloadEobPdf(
                `/lab/billing/era-reports/${remit.eraId}/claims/${remit.claimId}/eob`,
                `EOB_Claim_${remit.claimNumber || remit.claimId}.pdf`,
              )
              setEobLoading(false)
            }}>
            {eobLoading ? 'Print EOB…' : 'Print EOB'}
          </Button>
        )}
      </div>
      <div className="table-responsive">
        <table css={{ width: '100%', marginTop: 6 }}>
          <thead>
            <tr css={{ fontSize: '0.75rem', color: '#6c757d' }}>
              <th css={{ padding: '4px 8px' }}>CPT</th>
              <th css={{ padding: '4px 8px' }}>Serv. date</th>
              <th css={{ padding: '4px 8px' }}>Billed</th>
              <th css={{ padding: '4px 8px' }}>Allowed</th>
              <th css={{ padding: '4px 8px' }}>Paid</th>
              <th css={{ padding: '4px 8px' }}>Patient resp.</th>
              <th css={{ padding: '4px 8px' }}>Adjustments</th>
              <th css={{ padding: '4px 8px' }}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {remit.lines.map((line, index) => (
              <tr key={index}>
                <td css={cellCss}>{line.cptCode || '—'}</td>
                <td css={cellCss}>{formatCalendarDate(line.serviceDate, '—')}</td>
                <td css={cellCss}>{money(line.billed)}</td>
                <td css={cellCss}>{money(line.allowed)}</td>
                <td css={cellCss}>{money(line.paid)}</td>
                <td css={cellCss}>{money(line.patientResponsibility)}</td>
                <td css={cellCss}>
                  {line.adjustments.length
                    ? line.adjustments.map((adjustment) => `${adjustment.group}-${adjustment.reason} ${money(adjustment.amount)}`).join(', ')
                    : '—'}
                </td>
                <td css={cellCss}>{line.remarkCodes.join(', ') || '—'}</td>
              </tr>
            ))}
            <tr css={{ fontWeight: 600 }}>
              <td css={cellCss} colSpan={4}>
                Claim total
                {remit.remarkCodes.length ? (
                  <span className="text-muted" css={{ fontWeight: 400 }}> · Remarks {remit.remarkCodes.join(', ')}</span>
                ) : null}
              </td>
              <td css={cellCss}>{money(remit.paidAmount)}</td>
              <td css={cellCss}>{money(remit.patientResponsibility)}</td>
              <td css={cellCss} colSpan={2} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}
